// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xParserOptions } from "@rpsc/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";

const enum fields {
    termsText = "termsText",
    termsLinkText = "termsLinkText",
    termsLink = "termsLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.termsText]: string;
        [fields.termsLinkText]: string;
        [fields.termsLink]: LinkFieldValue;
    };
}

export interface TermsAndConditionsMessageStyles {
    container?: InjectableCss;
    termText?: TypographyPresentationProps;
    termsLink?: LinkPresentationProps;
}

export const termsAndConditionsMessageStyles: TermsAndConditionsMessageStyles = {};

const styles = termsAndConditionsMessageStyles;

const TermsAndConditionsMessage = ({ fields }: OwnProps) => {
    const termsUrl = useGetLink(fields.termsLink).url;

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.termText}>{parse(fields.termsText, xParserOptions)}</Typography>
            <Link href={termsUrl} {...styles.termsLink}>
                {translate(fields.termsLinkText)}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: TermsAndConditionsMessage,
    definition: {
        allowedContexts: ["CreateAccountPage", "GuestAccountRegistrationPage" as any],
        group: "Create Account",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.termsText,
                displayName: "Terms And Conditions Text",
                editorTemplate: "RichTextField",
                extendedConfig: { height: 100 },
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.termsLinkText,
                displayName: "Terms and Conditions Link Label",
                editorTemplate: "TextField",
                defaultValue: "Terms Of Use",
            },
            {
                fieldType: "General",
                name: fields.termsLink,
                displayName: "terms and Conditions Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
        ],
    },
};

export default widgetModule;
